import { push } from 'react-router-redux';
import { SubmissionError } from 'redux-form';
import {
  get as getSelf,
  isLoggedIn as isUserLoggedIn,
} from 'src/helpers/api/self';
import { create, CreatePagePayload, PageResource } from 'src/helpers/api/pages';
import { updateUserConsents } from 'src/helpers/api/preferencesService';
import {
  login,
  loginWithFacebook,
  registerAndLogin,
  changeName,
  isLoggedIn,
  ERROR_INVALID_CREDENTIALS,
  ERROR_EMAIL_ADDRESS_IN_USE,
  ChangeNameOptions,
  LoginOptions,
} from 'src/helpers/api/iam';
import { login as loginFacebookApi } from 'src/helpers/facebook';
import { Dispatch } from 'redux';
import { AppState } from 'src/redux/modules';
import { AnalyticsMeta } from 'src/redux/modules/analytics';
import { USER_CONSENTS } from 'config';
import { ConsentStatement } from 'src/components/Login/SignUp';
import { getAuth } from 'src/helpers/auth';

export const CREATE_START = 'CREATE_START';

interface CreateStartAction {
  type: 'CREATE_START';
  meta: {
    analytics: AnalyticsMeta;
  };
}

export function createPageStarted(): CreateStartAction {
  return {
    type: CREATE_START,
    meta: {
      analytics: {
        event: 'page view',
        subtype: 'create',
      },
    },
  };
}

interface CreateRequestAction {
  type: 'CREATE_REQUEST';
  payload: CreatePagePayload;
  meta: {
    analytics: AnalyticsMeta;
  };
}

interface CreateSuccessAction {
  type: 'CREATE_SUCCESS';
  payload: PageResource;
}

interface CreateFailureAction {
  type: 'CREATE_FAILURE';
  error: true;
  payload: Error;
}

interface CreateCheckpointAction {
  type: 'CREATE_CHECKPOINT';
  payload: CreatePagePayload;
}

interface CreateAbortedAction {
  type: 'CREATE_ABORTED';
}

export function createPage(
  page: CreatePagePayload,
  isCharityEndorsed: boolean
) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const analytics = {
      event: 'link click',
      subtype: 'button',
      event_value: 'continue',
      page_section: 'page creation > bottom',
      ...(isCharityEndorsed
        ? {
            actor_sources: 'charity endorsed checkbox',
            actor_id: page.charityId
              ? page.commitmentType
              : 'checkbox not ticked',
          }
        : {}),
    };

    dispatch<CreateRequestAction>({
      type: 'CREATE_REQUEST',
      payload: page,
      meta: {
        analytics,
      },
    });

    dispatch<CreateCheckpointAction>({
      type: 'CREATE_CHECKPOINT',
      payload: page,
    });

    if (__CLIENT__ && !isLoggedIn()) {
      dispatch<CreateAbortedAction>({ type: 'CREATE_ABORTED' });
      dispatch(push('/create/login'));
      return;
    }

    try {
      await advance(dispatch, getState);
    } catch (error) {
      dispatch<CreateFailureAction>({
        type: 'CREATE_FAILURE',
        error: true,
        payload: error,
      });
    }
  };
}

async function advance(
  dispatch: Dispatch<AppState>,
  getState: () => AppState,
  beforeRedirect?: () => void
) {
  const user = await getSelf();

  if (!user || !isUserLoggedIn(user)) {
    throw new Error(
      'Expected user to be logged in when advancing page creation.'
    );
  }

  if (!user.firstName || !user.lastName) {
    if (beforeRedirect) {
      beforeRedirect();
    }

    dispatch(push('/create/aboutyou'));
  } else {
    const data = await finishPageCreation(dispatch, getState);

    if (beforeRedirect) {
      beforeRedirect();
    }

    dispatch(push(`/${data.name}/coverphoto`));
  }
}

async function finishPageCreation(
  dispatch: Dispatch<AppState>,
  getState: () => AppState
) {
  const {
    create: { page },
    consents: { userConsentStatements },
  } = getState();

  if (!page) {
    throw new Error("Can't finish page creation, page data not in store");
  }

  const data = await create(page);
  /**
   * updateUserConsents is called in the background
   * no need for error handling and waiting for the response
   */
  updateUserConsents({
    userConsentStatements,
    userGuid: data.ownerUserId,
    pageId: data.id,
  });

  dispatch<CreateSuccessAction>({ type: 'CREATE_SUCCESS', payload: data });

  return data;
}

interface CreateInitLoginRequestAction {
  type: 'CREATE_INIT_LOGIN_REQUEST';
  meta: {
    analytics: AnalyticsMeta;
  };
}

interface CreateInitLoginSuccessAction {
  type: 'CREATE_INIT_LOGIN_SUCCESS';
}

interface CreateInitLoginFailureAction {
  type: 'CREATE_INIT_LOGIN_FAILURE';
  error: true;
  payload: Error;
}

export function initSignUp() {
  return initLogin({ eventValue: 'signup', pageSection: 'create > signup' });
}

export function initLogin({
  eventValue,
  pageSection,
}: {
  eventValue?: string;
  pageSection?: string;
} = {}) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch<CreateInitLoginRequestAction>({
      type: 'CREATE_INIT_LOGIN_REQUEST',
      meta: {
        analytics: {
          event: 'page view',
          subtype: 'user login page',
          event_value: eventValue || 'login',
          page_section: pageSection || 'create > login',
        },
      },
    });

    try {
      const page = getState().create.page;

      if (!page || isLoggedIn()) {
        dispatch(push('/create'));
        return;
      }

      dispatch<CreateInitLoginSuccessAction>({
        type: 'CREATE_INIT_LOGIN_SUCCESS',
      });
    } catch (err) {
      dispatch<CreateInitLoginFailureAction>({
        type: 'CREATE_INIT_LOGIN_FAILURE',
        error: true,
        payload: err,
      });
      dispatch(push('/create'));
    }
  };
}

interface CreateInitAboutYouRequestAction {
  type: 'CREATE_INIT_ABOUTYOU_REQUEST';
  meta: {
    analytics: AnalyticsMeta;
  };
}

interface CreateInitAboutYouSuccessAction {
  type: 'CREATE_INIT_ABOUTYOU_SUCCESS';
  payload: {
    userId: string;
    emailAddress: string;
    firstName: string;
    lastName: string;
    profileImage: string;
  };
}

interface CreateInitAboutYouFailureAction {
  type: 'CREATE_INIT_ABOUTYOU_FAILURE';
  error: true;
  payload: Error;
}

export function initAboutYou() {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch<CreateInitAboutYouRequestAction>({
      type: 'CREATE_INIT_ABOUTYOU_REQUEST',
      meta: {
        analytics: {
          event: 'page view',
          subtype: 'user login page',
          event_value: 'login',
          page_section: 'create > about you',
        },
      },
    });

    try {
      const page = getState().create.page;
      const user = await getSelf();
      const auth = getAuth();
      const accessToken = auth.getAccessToken();

      if (!page || !user || !isUserLoggedIn(user) || !accessToken) {
        dispatch(push('/create'));
        return;
      }

      dispatch<CreateInitAboutYouSuccessAction>({
        type: 'CREATE_INIT_ABOUTYOU_SUCCESS',
        payload: {
          userId: user.id,
          emailAddress: accessToken.email,
          firstName: user.firstName,
          lastName: user.lastName,
          profileImage: user.profileImage,
        },
      });
    } catch (err) {
      dispatch<CreateInitAboutYouFailureAction>({
        type: 'CREATE_INIT_ABOUTYOU_FAILURE',
        error: true,
        payload: err,
      });
      dispatch(push('/create'));
    }
  };
}

interface SubmitLoginRequestAction {
  type: 'SUBMIT_LOGIN_REQUEST';
  payload: {
    emailAddress: string;
    password: string;
  };
  meta: {
    analytics: AnalyticsMeta;
  };
}

interface SubmitLoginSuccessAction {
  type: 'SUBMIT_LOGIN_SUCCESS';
  payload: {
    emailAddress: string;
  };
}

interface SubmitLoginFailureAction {
  type: 'SUBMIT_LOGIN_FAILURE';
  error: true;
  payload: Error;
}

const ERROR_MAP: Record<string, string> = {
  [ERROR_INVALID_CREDENTIALS]: 'The email address or password is incorrect',
  ERROR_NO_CONSUMER_PERMISSIONS:
    'This email is already in use for a charity account. Please use your personal email.',
};

export function submitLogin({ emailAddress, password }: LoginOptions) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch<SubmitLoginRequestAction>({
      type: 'SUBMIT_LOGIN_REQUEST',
      payload: { emailAddress, password },
      meta: {
        analytics: {
          event: 'link click',
          subtype: 'user login page',
          event_value: 'sign in',
          page_section: 'create > login',
        },
      },
    });

    try {
      const { success, error } = await login({ emailAddress, password });

      if (success) {
        await advance(dispatch, getState, function beforeRedirect() {
          dispatch<SubmitLoginSuccessAction>({
            type: 'SUBMIT_LOGIN_SUCCESS',
            payload: { emailAddress },
          });
          refreshChrome();
        });
      } else {
        dispatch<SubmitLoginFailureAction>({
          type: 'SUBMIT_LOGIN_FAILURE',
          error: true,
          payload: new Error(error),
        });
        throw new SubmissionError({
          _error:
            ERROR_MAP[error!] || 'An error has occurred, please try again.',
        });
      }
    } catch (err) {
      if (err instanceof SubmissionError) {
        throw err;
      }

      dispatch<SubmitLoginFailureAction>({
        type: 'SUBMIT_LOGIN_FAILURE',
        error: true,
        payload: err,
      });
      throw new SubmissionError({
        _error: 'An error has occurred, please try again.',
      });
    }
  };
}

interface SubmitLoginFacebookRequestAction {
  type: 'SUBMIT_LOGIN_FACEBOOK_REQUEST';
  meta: {
    analytics: AnalyticsMeta;
  };
}

interface SubmitLoginFacebookSuccessAction {
  type: 'SUBMIT_LOGIN_FACEBOOK_SUCCESS';
  payload: {
    emailAddress: string;
  };
}

interface SubmitLoginFacebookFailureAction {
  type: 'SUBMIT_LOGIN_FACEBOOK_FAILURE';
  payload: {
    errorMessage: string;
  };
}

const DEFAULT_ERROR_MESSAGE =
  'An error occurred trying to connect with Facebook. Please try again, or create an account using email address and password.';

export function submitLoginFacebook() {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch<SubmitLoginFacebookRequestAction>({
      type: 'SUBMIT_LOGIN_FACEBOOK_REQUEST',
      meta: {
        analytics: {
          event: 'link click',
          subtype: 'user login page',
          event_value: 'continue with facebook',
          page_section: 'create > login',
        },
      },
    });

    try {
      const user = await loginFacebookApi();

      if (!user || !user.emailAddress) {
        dispatch<SubmitLoginFacebookFailureAction>({
          type: 'SUBMIT_LOGIN_FACEBOOK_FAILURE',
          payload: { errorMessage: DEFAULT_ERROR_MESSAGE },
        });
        return;
      }

      const { success } = await loginWithFacebook(user);

      if (success) {
        await advance(dispatch, getState, function doStuff() {
          dispatch<SubmitLoginFacebookSuccessAction>({
            type: 'SUBMIT_LOGIN_FACEBOOK_SUCCESS',
            payload: { emailAddress: user.emailAddress },
          });
        });
      } else {
        dispatch<SubmitLoginFacebookFailureAction>({
          type: 'SUBMIT_LOGIN_FACEBOOK_FAILURE',
          payload: { errorMessage: DEFAULT_ERROR_MESSAGE },
        });
      }
    } catch (err) {
      dispatch<SubmitLoginFacebookFailureAction>({
        type: 'SUBMIT_LOGIN_FACEBOOK_FAILURE',
        payload: { errorMessage: DEFAULT_ERROR_MESSAGE },
      });
    }
  };
}

interface SignUpRequestAction {
  type: 'SIGN_UP_REQUEST';
  payload: {
    firstName: string;
    lastName: string;
    emailAddress: string;
    password: string;
  };
  meta: {
    analytics: AnalyticsMeta;
  };
}

interface SignUpSuccessAction {
  type: 'SIGN_UP_SUCCESS';
}

interface SignUpFailureAction {
  type: 'SIGN_UP_FAILURE';
  error: true;
  payload: Error;
}

export interface SignupOptions {
  firstName: string;
  lastName: string;
  emailAddress: string;
  password: string;
  userConsents: {
    collectionLocationId?: number;
    consents: ConsentStatement[];
  };
}

export function signUp({
  firstName,
  lastName,
  emailAddress,
  password,
  userConsents,
}: SignupOptions) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch<SignUpRequestAction>({
      type: 'SIGN_UP_REQUEST',
      payload: {
        firstName,
        lastName,
        emailAddress,
        password,
      },
      meta: {
        analytics: {
          event: 'link click',
          subtype: 'user login page',
          event_value: 'continue',
          page_section: 'create > signup',
        },
      },
    });

    try {
      const { success, error } = await registerAndLogin({
        firstName,
        lastName,
        emailAddress,
        password,
        userConsents: {
          collectionLocationId: USER_CONSENTS.COLLECTION_LOCATION_ID,
          consents: userConsents.consents.map(consent => ({
            entityId: USER_CONSENTS.ENTITY_ID,
            id: consent.id,
            consentGiven: !!consent.consentGiven,
            text: consent.text,
          })),
        },
      });

      if (success) {
        const data = await finishPageCreation(dispatch, getState);

        dispatch<SignUpSuccessAction>({ type: 'SIGN_UP_SUCCESS' });
        refreshChrome();
        dispatch(push(`/${data.name}/coverphoto`));
      } else if (error === ERROR_EMAIL_ADDRESS_IN_USE) {
        dispatch<SignUpFailureAction>({
          type: 'SIGN_UP_FAILURE',
          error: true,
          payload: new Error(error),
        });
        throw new SubmissionError({
          _error: 'The provided email address is already in use.',
        });
      } else {
        dispatch<SignUpFailureAction>({
          type: 'SIGN_UP_FAILURE',
          error: true,
          payload: new Error(error),
        });
        throw new SubmissionError({
          _error: 'An error has occurred, please try again.',
        });
      }
    } catch (err) {
      if (err instanceof SubmissionError) {
        throw err;
      }

      dispatch<SignUpFailureAction>({
        type: 'SIGN_UP_FAILURE',
        error: true,
        payload: err,
      });
      throw new SubmissionError({
        _error: 'An error has occurred, please try again.',
      });
    }
  };
}

interface CreateSubmitAboutYouRequestAction {
  type: 'CREATE_SUBMIT_ABOUTYOU_REQUEST';
  payload: {
    firstName: string;
    lastName: string;
  };
  meta: {
    analytics: AnalyticsMeta;
  };
}

interface CreateSubmitAboutYouSuccessAction {
  type: 'CREATE_SUBMIT_ABOUTYOU_SUCCESS';
}

interface CreateSubmitAboutYouFailureAction {
  type: 'CREATE_SUBMIT_ABOUTYOU_FAILURE';
  error: true;
  payload: Error;
}

export function submitAboutYou({ firstName, lastName }: ChangeNameOptions) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch<CreateSubmitAboutYouRequestAction>({
      type: 'CREATE_SUBMIT_ABOUTYOU_REQUEST',
      payload: { firstName, lastName },
      meta: {
        analytics: {
          event: 'link click',
          subtype: 'user login page',
          event_value: 'continue',
          page_section: 'create > about you',
        },
      },
    });

    try {
      const userId = getState().create.userId!;

      await changeName(userId, { firstName, lastName });

      const data = await finishPageCreation(dispatch, getState);

      dispatch<CreateSubmitAboutYouSuccessAction>({
        type: 'CREATE_SUBMIT_ABOUTYOU_SUCCESS',
      });
      dispatch(push(`/${data.name}/coverphoto`));
    } catch (err) {
      dispatch<CreateSubmitAboutYouFailureAction>({
        type: 'CREATE_SUBMIT_ABOUTYOU_FAILURE',
        error: true,
        payload: err,
      });
      throw new SubmissionError({
        _error: 'An error has occurred, please try again.',
      });
    }
  };
}

function refreshChrome() {
  if (__CLIENT__ && window.jg && window.jg.chrome) {
    window.jg.chrome.refresh();
  }
}

interface ForgotPasswordAction {
  type: 'FORGOT_PASSWORD';
  meta: {
    analytics: AnalyticsMeta;
  };
}

export function forgotPassword(): ForgotPasswordAction {
  return {
    type: 'FORGOT_PASSWORD',
    meta: {
      analytics: {
        event: 'link click',
        subtype: 'user login page',
        event_value: 'forgotten password',
        page_section: 'create > signup',
      },
    },
  };
}

interface CreateCategoryChanged {
  type: 'CREATE_CATEGORY_CHANGED';
  meta: {
    analytics: AnalyticsMeta;
  };
}

export function categoryChanged({
  categoryName,
}: {
  categoryName: string;
}): CreateCategoryChanged {
  return {
    type: 'CREATE_CATEGORY_CHANGED',
    meta: {
      analytics: {
        event: 'click',
        subtype: 'drop down',
        event_value: 'category',
        page_section: 'page creation',
        actor_id: categoryName,
        actor_source: 'drop_down',
        content_type: 'crowdfunding_guid',
        activity_type: 'crowdfunding_guid',
      },
    },
  };
}

type CreateAction =
  | CreateStartAction
  | CreateAbortedAction
  | CreateCheckpointAction
  | CreateRequestAction
  | CreateSuccessAction
  | CreateFailureAction
  | CreateInitLoginRequestAction
  | CreateInitLoginSuccessAction
  | CreateInitLoginFailureAction
  | CreateInitAboutYouRequestAction
  | CreateInitAboutYouSuccessAction
  | CreateInitAboutYouFailureAction
  | SubmitLoginRequestAction
  | SubmitLoginSuccessAction
  | SubmitLoginFailureAction
  | SubmitLoginFacebookRequestAction
  | SubmitLoginFacebookSuccessAction
  | SubmitLoginFacebookFailureAction
  | SignUpRequestAction
  | SignUpSuccessAction
  | SignUpFailureAction
  | CreateSubmitAboutYouRequestAction
  | CreateSubmitAboutYouSuccessAction
  | CreateSubmitAboutYouFailureAction
  | ForgotPasswordAction
  | CreateCategoryChanged;

export interface CreateState {
  userId?: string;
  isStateVerified: boolean;
  emailAddress: string | null;
  errorMessage: string | null;
  isRequesting: boolean;
  isRequestingFacebook: boolean;
  page: CreatePagePayload | null;
  hasError?: boolean;
  firstName?: string;
  lastName?: string;
  profileImage?: string;
}

const initialState: CreateState = {
  isStateVerified: false,
  emailAddress: null,
  errorMessage: null,
  isRequesting: false,
  isRequestingFacebook: false,
  page: null,
};

export default function reducer(
  state: CreateState = initialState,
  action: CreateAction
): CreateState {
  switch (action.type) {
    case 'CREATE_REQUEST':
      return {
        ...state,
        isRequesting: true,
      };

    case 'CREATE_CHECKPOINT':
      return {
        ...state,
        page: action.payload,
      };

    case 'CREATE_ABORTED':
      return {
        ...state,
        isRequesting: false,
      };

    case 'CREATE_SUCCESS':
      return {
        ...state,
        page: null,
        isRequesting: false,
      };

    case 'CREATE_FAILURE':
      return {
        ...state,
        isRequesting: false,
        hasError: true,
        errorMessage: action.payload.message,
      };

    case 'CREATE_INIT_LOGIN_SUCCESS':
      return {
        ...state,
        isRequesting: false,
        isRequestingFacebook: false,
      };

    case 'SUBMIT_LOGIN_REQUEST':
      return {
        ...state,
        isRequesting: true,
      };

    case 'SUBMIT_LOGIN_SUCCESS':
      return {
        ...state,
        emailAddress: action.payload.emailAddress,
        isRequesting: false,
      };

    case 'SUBMIT_LOGIN_FAILURE':
      return {
        ...state,
        isRequesting: false,
      };

    case 'SUBMIT_LOGIN_FACEBOOK_REQUEST':
      return {
        ...state,
        errorMessage: null,
        isRequestingFacebook: true,
      };

    case 'SUBMIT_LOGIN_FACEBOOK_SUCCESS':
      return {
        ...state,
        emailAddress: action.payload.emailAddress,
        isRequestingFacebook: false,
      };

    case 'SUBMIT_LOGIN_FACEBOOK_FAILURE':
      return {
        ...state,
        errorMessage: action.payload.errorMessage,
        isRequestingFacebook: false,
      };

    case 'CREATE_INIT_ABOUTYOU_SUCCESS':
      return {
        ...state,
        userId: action.payload.userId,
        emailAddress: action.payload.emailAddress,
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        profileImage: action.payload.profileImage,
        isRequesting: false,
      };

    case 'CREATE_SUBMIT_ABOUTYOU_REQUEST':
      return {
        ...state,
        isRequesting: true,
      };

    case 'CREATE_SUBMIT_ABOUTYOU_SUCCESS':
    case 'CREATE_SUBMIT_ABOUTYOU_FAILURE':
      return {
        ...state,
        isRequesting: false,
      };

    case 'SIGN_UP_REQUEST':
      return {
        ...state,
        isRequesting: true,
      };

    case 'SIGN_UP_SUCCESS':
    case 'SIGN_UP_FAILURE':
      return {
        ...state,
        isRequesting: false,
      };

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/redux/modules/create.ts